import { useCallback, useState, useRef, DragEvent, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowInstance,
  OnSelectionChangeParams,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { AppShell } from '@/components/layout/AppShell';
import { DesktopRequired } from '@/components/layout/DesktopRequired';
import { Button } from '@/components/ui';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  Save,
  Play,
  ArrowLeft,
  PanelLeft,
  PanelRight,
  PanelBottom,
  Undo,
  Redo,
  LayoutGrid,
  Maximize2,
} from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { nodeTypes } from '@/components/flow-editor/nodes';
import { edgeTypes } from '@/components/flow-editor/edges';
import { NodePalette } from '@/components/flow-editor/NodePalette';
import { PropertiesPanel } from '@/components/flow-editor/PropertiesPanel';
import { BottomPanel } from '@/components/flow-editor/BottomPanel';
import type { ValidationIssue } from '@/components/flow-editor/ValidationPanel';
import { FlowEditorProvider, useFlowEditor } from '@/contexts/FlowEditorContext';
import { useToast } from '@/contexts/ToastContext';
import { isFlowRunnable } from '@/services/flowService';
import { ViewToggle } from '@/components/flow-editor/ViewToggle';
import { CodeView } from '@/components/flow-editor/CodeView';
import { Modem } from '@/types/firestore';
import { getLayoutedElements } from '@/utils/layoutUtils';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';

function FlowEditorLayout({ children }: { children: (sidebarOffset: string) => React.ReactNode }) {
  const { state: sidebarState, setOpen } = useSidebar();
  const sidebarOffset = sidebarState === 'expanded' ? '14rem' : '3rem';

  // Auto-collapse the app nav on mount; restore on unmount.
  useEffect(() => {
    const wasExpanded = sidebarState === 'expanded';
    if (wasExpanded) setOpen(false);
    return () => {
      if (wasExpanded) setOpen(true);
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children(sidebarOffset)}</>;
}

function FlowEditorInner({ isPrimitive = false }: { isPrimitive?: boolean }) {
  const navigate = useNavigate();
  const { flowId: urlFlowId, primitiveId } = useParams();
  const flowId = isPrimitive ? primitiveId : urlFlowId;
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <AppShell>
        <DesktopRequired
          feature={isPrimitive ? 'Primitive Editor' : 'Flow Editor'}
          backTo={isPrimitive ? '/admin/primitives' : '/flows'}
          backLabel={isPrimitive ? 'Back to Primitives' : 'Back to Flows'}
        />
      </AppShell>
    );
  }
  const { theme } = useTheme();
  const { activeOrganization } = useAuth();
  const fmt = useDateFormat();
  const {
    nodes,
    edges,
    flowName,
    flowDescription,
    flowStatus,
    hasUnsavedChanges,
    isSaving,
    lastSaved,
    canUndo,
    canRedo,
    validationIssues,
    hasErrors,
    capabilityRequirements,
    activeView,
    codeContent,
    codeErrors,
    setNodes,
    setEdges,
    setFlowName,
    setFlowDescription,
    setFlowStatus,
    updateNodeData,
    onConnect: contextOnConnect,
    undo,
    redo,
    save,
    setCapabilityRequirements,
    setActiveView,
    updateCodeContent,
  } = useFlowEditor();

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);

  // Inline name / description editing in the header
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  const startEditName = () => { setNameDraft(flowName); setEditingName(true); };
  const commitName = () => {
    const next = nameDraft.trim();
    if (next && next !== flowName) setFlowName(next);
    setEditingName(false);
  };
  const startEditDesc = () => { setDescDraft(flowDescription); setEditingDesc(true); };
  const commitDesc = () => {
    const next = descDraft.trim();
    if (next !== flowDescription) setFlowDescription(next);
    setEditingDesc(false);
  };

  // Publish / unpublish (writes immediately — not part of the graph Save)
  const { success, error: toastError } = useToast();
  const [statusSaving, setStatusSaving] = useState(false);
  const handleToggleStatus = async () => {
    if (statusSaving) return;
    const publish = !isFlowRunnable(flowStatus);
    setStatusSaving(true);
    try {
      await setFlowStatus(publish ? 'published' : 'draft');
      success(publish ? 'Flow published — now selectable in the Run/Schedule picker' : 'Moved to draft — hidden from the Run/Schedule picker');
    } catch (err) {
      console.error('Status update failed:', err);
      toastError('Could not update flow status');
    } finally {
      setStatusSaving(false);
    }
  };
  const [bottomPanelTab, setBottomPanelTab] = useState<'validation' | 'execution' | 'settings'>('validation');

  // Visibility toggles — fixed widths when shown, hidden when collapsed.
  const [showPalette, setShowPalette] = useState(true);
  const [showInspector, setShowInspector] = useState(true);
  const [showBottom, setShowBottom] = useState(true);

  // Bottom panel height in pixels — user-draggable via the handle above it.
  // Bounds: 120 (min usable) to 80% of viewport (don't eat the toolbar).
  const [bottomHeight, setBottomHeight] = useState(224);
  const isDraggingBottom = useRef(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDraggingBottom.current) return;
      e.preventDefault();
      const next = window.innerHeight - e.clientY - 12; // 12 = toolbar offset fudge
      const max = Math.floor(window.innerHeight * 0.8);
      setBottomHeight(Math.min(max, Math.max(120, next)));
    };
    const onUp = () => {
      if (!isDraggingBottom.current) return;
      isDraggingBottom.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Pending navigation captured when user hits Back with unsaved changes.
  // Cleared by the leave-prompt dialog's actions.
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  const [modems, setModems] = useState<Modem[]>([]);

  useEffect(() => {
    if (!activeOrganization?.id) return;
    const modemsQuery = query(
      collection(db, 'modems'),
      where('organizationId', '==', activeOrganization.id)
    );
    const unsubscribe = onSnapshot(
      modemsQuery,
      (snapshot) => {
        setModems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Modem)));
      },
      (error) => console.error('Error fetching modems:', error)
    );
    return () => unsubscribe();
  }, [activeOrganization?.id]);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const onNodesChange = useCallback((changes: any) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, [setNodes]);

  const onEdgesChange = useCallback((changes: any) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, [setEdges]);

  const onConnect = useCallback((connection: any) => {
    contextOnConnect(connection);
  }, [contextOnConnect]);

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowInstance) return;
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const getDefaultData = (nodeType: string): Record<string, any> => {
        switch (nodeType) {
          case 'start':
          case 'end':
            return {};
          case 'atCommand':
            return { atCommand: { command: '', expectedResponse: 'OK', timeout: 5000, retries: 2, parseResponse: true } };
          case 'delay':
            return { delay: { duration: 1, unit: 's' } };
          case 'condition':
            return { condition: { expression: '' } };
          case 'loop':
            return { loop: { iteratorExpression: '', itemVariable: 'item', indexVariable: 'index', maxIterations: 100 } };
          case 'parallel':
            return { parallel: { branches: [], waitForAll: true } };
          case 'variable':
            return { variable: { name: '', value: '', type: 'string' } };
          case 'assertion':
            return { assertion: { expression: '', message: '', severity: 'error' } };
          case 'primitiveCall':
            return { primitiveCall: { primitiveId: '', inputMapping: {}, outputMapping: {} } };
          case 'log':
            return { log: { message: '', level: 'info' } };
          case 'dataTest':
            return { dataTest: { testType: 'ping', target: '8.8.8.8', timeout: 60000 } };
          case 'voiceTest':
            return { voiceTest: { kind: 'ims_status' } };
          case 'voiceAgentTest':
            return {
              voiceAgentTest: {
                phoneNumber: '',
                maxDurationSec: 150,
                agentConfig: {
                  goal: '',
                  maxTurns: 16,
                  allowSpeak: false,
                  maxDtmfDigits: 64,
                  assertions: [{ type: 'goal_achieved' }],
                },
              },
            };
          default:
            return {};
        }
      };
      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: getDefaultData(type),
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const nextNode = params.nodes[0] || null;
    const nextEdge = params.edges[0] || null;
    setSelectedNode(nextNode);
    setSelectedEdge(nextEdge);
    if (nextNode || nextEdge) setShowInspector(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      await save();
    } catch (error) {
      console.error('Save failed:', error);
    }
  }, [save]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (activeView === 'code') {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault();
          handleSave();
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping) {
        e.preventDefault();
        if (selectedNode) {
          setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
          setEdges((eds) => eds.filter((ed) => ed.source !== selectedNode.id && ed.target !== selectedNode.id));
          setSelectedNode(null);
        } else if (selectedEdge) {
          setEdges((eds) => eds.filter((ed) => ed.id !== selectedEdge.id));
          setSelectedEdge(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, selectedNode, selectedEdge, setNodes, setEdges, activeView, handleSave]);

  const handleRun = () => {
    setBottomPanelTab('execution');
    setShowBottom(true);
  };

  const handleAutoArrange = useCallback(() => {
    const { nodes: layoutedNodes } = getLayoutedElements(nodes, edges, {
      direction: 'TB',
      nodeWidth: 180,
      nodeHeight: 60,
      nodeSep: 32,
      rankSep: 70,
    });
    setNodes(layoutedNodes);
    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.1, maxZoom: 1 });
    }, 50);
  }, [nodes, edges, setNodes, reactFlowInstance]);

  const handleIssueClick = useCallback((issue: ValidationIssue) => {
    if (issue.nodeId) {
      const node = nodes.find((n) => n.id === issue.nodeId);
      if (node && reactFlowInstance) {
        reactFlowInstance.fitView({ nodes: [{ id: node.id }], padding: 0.5, duration: 300 });
        setSelectedNode(node);
      }
    } else if (issue.edgeId) {
      const edge = edges.find((e) => e.id === issue.edgeId);
      if (edge) setSelectedEdge(edge);
    }
  }, [nodes, edges, reactFlowInstance]);

  const errorCount = validationIssues.filter((i) => i.severity === 'error').length;
  const warningCount = validationIssues.filter((i) => i.severity === 'warning').length;
  const bottomStatus = hasErrors
    ? `${errorCount} error${errorCount === 1 ? '' : 's'}${warningCount ? ` · ${warningCount} warning${warningCount === 1 ? '' : 's'}` : ''}`
    : warningCount
      ? `${warningCount} warning${warningCount === 1 ? '' : 's'}`
      : 'Ready';

  return (
    <AppShell>
      <FlowEditorLayout>
        {(sidebarOffset) => (
          <div
            className="fixed inset-0 top-16 transition-[left] duration-200 ease-linear flex flex-col bg-background"
            style={{ left: sidebarOffset }}
          >
            {/* Docked toolbar */}
            <div className="h-12 flex items-center gap-2 border-b border-border bg-card px-2 flex-shrink-0">
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<ArrowLeft className="w-4 h-4" />}
                  onClick={() => {
                    const target = isPrimitive ? '/admin/primitives' : '/flows';
                    if (hasUnsavedChanges) {
                      setPendingNav(target);
                    } else {
                      navigate(target);
                    }
                  }}
                  title={isPrimitive ? 'Back to Primitives' : 'Back to Flows'}
                />
                <div className="h-6 w-px bg-border mx-1" />
                <ViewToggle
                  activeView={activeView}
                  onViewChange={setActiveView}
                  hasCodeErrors={codeErrors.length > 0}
                />
                {activeView === 'visual' && (
                  <>
                    <div className="h-6 w-px bg-border mx-1" />
                    <Button
                      variant={showPalette ? 'secondary' : 'ghost'}
                      size="sm"
                      icon={<PanelLeft className="w-4 h-4" />}
                      onClick={() => setShowPalette((v) => !v)}
                      title="Toggle Node Library"
                    />
                    <Button
                      variant={showInspector ? 'secondary' : 'ghost'}
                      size="sm"
                      icon={<PanelRight className="w-4 h-4" />}
                      onClick={() => setShowInspector((v) => !v)}
                      title="Toggle Inspector"
                    />
                    <Button
                      variant={showBottom ? 'secondary' : 'ghost'}
                      size="sm"
                      icon={<PanelBottom className="w-4 h-4" />}
                      onClick={() => setShowBottom((v) => !v)}
                      title="Toggle Bottom Panel"
                    />
                    <div className="h-6 w-px bg-border mx-1" />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Undo className="w-4 h-4" />}
                      onClick={undo}
                      disabled={!canUndo}
                      title="Undo (Ctrl+Z)"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Redo className="w-4 h-4" />}
                      onClick={redo}
                      disabled={!canRedo}
                      title="Redo (Ctrl+Y)"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<LayoutGrid className="w-4 h-4" />}
                      onClick={handleAutoArrange}
                      title="Auto-arrange nodes"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Maximize2 className="w-4 h-4" />}
                      onClick={() => reactFlowInstance?.fitView({ padding: 0.2, maxZoom: 1, duration: 200 })}
                      title="Fit flow to view"
                    />
                  </>
                )}
              </div>

              {/* Center: editable title + description — grows to fill the toolbar */}
              <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5 px-1">
                {editingName ? (
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={commitName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitName(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingName(false); }
                    }}
                    className="w-full max-w-md bg-background border border-input rounded px-2 py-1 text-sm font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder={isPrimitive ? 'Primitive name' : 'Flow name'}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={startEditName}
                    className="block w-fit max-w-full text-left text-sm font-semibold text-foreground truncate leading-tight rounded px-1 -mx-1 hover:bg-muted"
                    title="Click to rename"
                  >
                    {flowName}
                  </button>
                )}

                {editingDesc ? (
                  <input
                    autoFocus
                    value={descDraft}
                    onChange={(e) => setDescDraft(e.target.value)}
                    onBlur={commitDesc}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitDesc(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); }
                    }}
                    className="w-full max-w-md bg-background border border-input rounded px-2 py-0.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Add a description"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={startEditDesc}
                    className="block w-fit max-w-full text-left text-xs text-muted-foreground truncate leading-tight rounded px-1 -mx-1 hover:bg-muted hover:text-foreground"
                    title="Click to edit description"
                  >
                    {flowDescription || 'Add description…'}
                  </button>
                )}
              </div>

              {/* Right: save status + actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {!isPrimitive && (
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    disabled={statusSaving}
                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors disabled:opacity-60 ${
                      isFlowRunnable(flowStatus)
                        ? 'border-accent-green/30 text-accent-green hover:bg-accent-green/10'
                        : 'border-accent-yellow/40 text-accent-yellow hover:bg-accent-yellow/10'
                    }`}
                    title={isFlowRunnable(flowStatus)
                      ? 'Published — click to move to draft (hides it from the Run/Schedule picker)'
                      : 'Draft — click to publish (makes it selectable in the Run/Schedule picker)'}
                  >
                    {statusSaving ? '…' : isFlowRunnable(flowStatus) ? 'Published' : 'Draft'}
                  </button>
                )}
                {hasUnsavedChanges ? (
                  <span className="text-[11px] text-accent-yellow whitespace-nowrap">• Unsaved</span>
                ) : lastSaved ? (
                  <span className="text-[11px] text-muted-foreground/70 whitespace-nowrap">
                    Saved {fmt.formatTime(lastSaved)}
                  </span>
                ) : null}
                <div className="h-6 w-px bg-border" />
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Save className="w-4 h-4" />}
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  icon={<Play className="w-4 h-4" />}
                  onClick={handleRun}
                >
                  Run
                </Button>
              </div>
            </div>

            {/* Three-region docked layout — plain flex, no library */}
            <div className="flex-1 min-h-0 flex">
              {activeView === 'visual' && showPalette && (
                <div className="w-64 flex-shrink-0 border-r border-border bg-card overflow-hidden">
                  <NodePalette isPrimitive={isPrimitive} />
                </div>
              )}

              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex-1 min-h-0">
                  {activeView === 'visual' ? (
                    <div
                      ref={reactFlowWrapper}
                      className="w-full h-full relative"
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                    >
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setReactFlowInstance}
                        onDragOver={onDragOver}
                        onDrop={onDrop}
                        onSelectionChange={onSelectionChange}
                        fitView
                        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
                        defaultEdgeOptions={{ style: { strokeWidth: 3, opacity: 1 } }}
                        proOptions={{ hideAttribution: true }}
                        style={{ backgroundColor: theme === 'dark' ? '#0a0f1e' : '#f8f9fa' }}
                      >
                        <Background
                          variant={BackgroundVariant.Dots}
                          gap={16}
                          size={1}
                          color={theme === 'dark' ? '#1e2942' : '#e5e7eb'}
                        />
                        <Controls />
                      </ReactFlow>
                    </div>
                  ) : (
                    <div className="w-full h-full">
                      <CodeView
                        value={codeContent}
                        onChange={updateCodeContent}
                        onValidate={() => { /* errors flow through context */ }}
                        isPrimitive={isPrimitive}
                      />
                    </div>
                  )}
                </div>

                {activeView === 'visual' && showBottom && (
                  <div
                    role="separator"
                    aria-orientation="horizontal"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      isDraggingBottom.current = true;
                      document.body.style.cursor = 'ns-resize';
                      document.body.style.userSelect = 'none';
                    }}
                    className="h-1 flex-shrink-0 cursor-ns-resize bg-border hover:bg-accent-blue/60 transition-colors"
                    title="Drag to resize"
                  />
                )}

                {activeView === 'visual' && (
                  <div
                    className="flex-shrink-0 border-t border-border bg-card overflow-hidden"
                    style={{ height: showBottom ? bottomHeight : 28 }}
                  >
                    <BottomPanel
                      collapsed={!showBottom}
                      onExpandRequest={() => setShowBottom(true)}
                      onCollapseRequest={() => setShowBottom(false)}
                      statusLine={bottomStatus}
                      activeTab={bottomPanelTab}
                      onActiveTabChange={setBottomPanelTab}
                      validationIssues={validationIssues}
                      onIssueClick={handleIssueClick}
                      flowId={flowId || ''}
                      nodes={nodes}
                      edges={edges}
                      modems={modems}
                      onHighlightNode={setHighlightedNodeId}
                      isPrimitive={isPrimitive}
                      hasValidationErrors={hasErrors}
                      capabilityRequirements={capabilityRequirements}
                      onCapabilityRequirementsChange={setCapabilityRequirements}
                    />
                  </div>
                )}
              </div>

              {activeView === 'visual' && showInspector && (
                <div className="w-80 flex-shrink-0 border-l border-border bg-card overflow-hidden">
                  <PropertiesPanel
                    selectedNode={selectedNode}
                    selectedEdge={selectedEdge}
                    onUpdateNode={updateNodeData}
                    onClose={() => {
                      setSelectedNode(null);
                      setSelectedEdge(null);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </FlowEditorLayout>

      <AlertDialog open={pendingNav !== null} onOpenChange={(open) => !open && setPendingNav(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to <span className="font-semibold text-foreground">{flowName}</span>.
              Save before leaving, or discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPendingNav(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                const target = pendingNav;
                setPendingNav(null);
                if (target) navigate(target);
              }}
            >
              Discard &amp; leave
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={isSaving}
              onClick={async () => {
                const target = pendingNav;
                try {
                  await save();
                  setPendingNav(null);
                  if (target) navigate(target);
                } catch (err) {
                  console.error('Save failed before navigation:', err);
                  // Leave the dialog open; user can retry or discard.
                }
              }}
            >
              {isSaving ? 'Saving…' : 'Save & leave'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

export function FlowEditor({ isPrimitive = false }: { isPrimitive?: boolean }) {
  return (
    <FlowEditorProvider isPrimitive={isPrimitive}>
      <FlowEditorInner isPrimitive={isPrimitive} />
    </FlowEditorProvider>
  );
}
